import {
	ChangeDetectorRef,
	Component,
	ElementRef,
	inject,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Renderer2,
	SimpleChanges,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import {
	AlertComponent,
	ButtonComponent,
	CardAccessComponent,
	CheckboxComponent,
	CollapseComponent,
	INavHelp,
	ISelect,
	ItemComponent,
	ITagImage,
	IToogleSelect,
	InputComponent,
	LinkComponent,
	ListDetailComponent,
	NavHelpComponent,
	FormGroupComponent,
	ProgressComponent,
	PwcsLayoutComponent,
	ScanFaceComponent,
	SelectComponent,
	SpinnerComponent,
	TagComponent,
	TileComponent,
	ToggleComponent,
	TopnavComponent,
	WatermarkComponent,
} from '@tecalisdev/infinity';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { DesignThemeSection } from '../../models/design.models';
import { DesignHelperService } from '../../services/design-helper.service';
import { themeLottieAnimationData } from '../../services/lottie/preview-lottie-theme.util';

export interface IThemeStyles {
	'--logo'?: string;
	'--typography'?: string;
	/** Alias preview de Principal (= --text-strong). */
	'--text-color'?: string;
	/**
	 * Admin Principal — nombre propio (como title/secondary/tertiary).
	 * Infinity `.dark` redefine `--text-strong` / `--text-color` en el layout.
	 */
	'--text-color-primary'?: string;
	'--text-color-title'?: string;
	'--text-color-secondary'?: string;
	'--text-color-tertiary'?: string;
	/** Tokens Infinity (NIF-751 Carmen: Principal → text/strong). */
	'--text-strong'?: string;
	'--text-primary'?: string;
	'--text-secondary'?: string;
	'--text-tertiary'?: string;
	'--button-primary-text-color'?: string;
	'--button-primary-text-hover-color'?: string;
	'--button-primary-border-color'?: string;
	'--button-primary-border-hover-color'?: string;
	'--button-primary-background-color'?: string;
	'--button-primary-background-hover-color'?: string;
	'--button-secondary-text-color'?: string;
	'--button-secondary-text-hover-color'?: string;
	'--button-secondary-border-color'?: string;
	'--button-secondary-border-hover-color'?: string;
	'--button-secondary-background-color'?: string;
	'--button-secondary-background-hover-color'?: string;
	'--button-border-radius'?: string;
	'--button-icon-border-radius'?: string;
	'--button-icon-color'?: string;
	'--action-on-icon'?: string;
	'--button-icon-color-hover'?: string;
	'--button-icon-color-active'?: string;
	'--button-icon-background-color'?: string;
	'--button-icon-background-color-hover'?: string;
	'--button-icon-background-color-active'?: string;
	'--link-color'?: string;
	'--link-hover-color'?: string;
	'--link-text-decoration'?: string;
	'--link-text-decoration-hover'?: string;
	'--action-link'?: string;
	'--action-link-hover'?: string;
	/** Infinity checkbox / actions (mismo hex que botón primario). */
	'--action-primary'?: string;
	'--action-primary-border'?: string;
	'--action-primary-hover'?: string;
	'--action-on-primary'?: string;
	'--general-border-radius'?: string;
	/** Avanzado: radios Infinity (contenedor / secundario / tag / checkbox). */
	'--pwcs-radius-general'?: string;
	'--radius-container'?: string;
	'--radius-container-sm'?: string;
	'--radius-tag'?: string;
	'--radius-checkbox'?: string;
	'--icon-border'?: string;
	'--surface-inverse'?: string;
	'--icon-background'?: string;
	'--surface-brand-primary'?: string;
	'--icon-primary'?: string;
	'--icon-highlight'?: string;
	'--icon-brand-primary'?: string;
	'--icon-brand-secondary'?: string;
	'--icon-secondary-border'?: string;
	'--surface-brand-tertiary'?: string;
	'--icon-secondary-background'?: string;
	'--surface-brand-secondary'?: string;
	'--icon-secondary'?: string;
	'--icon-brand-tertiary'?: string;
	/** Alias PWCS para SVG home-pol / connection-escrituras-tutorial. */
	'--pwcs-icon-border'?: string;
	'--pwcs-icon-background'?: string;
	'--pwcs-icon-primary'?: string;
	'--pwcs-icon-secondary-border'?: string;
	'--pwcs-icon-secondary-background'?: string;
	'--pwcs-icon-secondary'?: string;
	'--lottie-frame-border'?: string;
	'--lottie-frame-icon-border'?: string;
	'--lottie-frame-bg'?: string;
	'--pwcs-lottie-frame-border'?: string;
	'--pwcs-lottie-frame-icon-border'?: string;
	'--pwcs-lottie-frame-bg'?: string;
	'--pwcs-progress-fill'?: string;
	'--brand-primary'?: string;
	'--progress-border-radius'?: string;
	/** Guía de captura → stroke del óvalo `tec-scan-face` (token Infinity). */
	'--feedback-info-solid'?: string;
	'--capture-guide-color'?: string;
	/** Alert warning (Marca / tec-alert status=warning) — mismos tokens que Feedback Alerta. */
	'--feedback-info-surface'?: string;
	'--feedback-on-info-surface'?: string;
	'--feedback-warning-surface'?: string;
	'--feedback-on-warning-surface'?: string;
	'--feedback-success-surface'?: string;
	'--feedback-on-success-surface'?: string;
	'--feedback-error-surface'?: string;
	'--feedback-on-error-surface'?: string;
	/**
	 * Espejos anti-`.dark` Feedback (Infinity redefine `--feedback-*-surface` en el layout).
	 * `.preview-docs` / Avanzado / brand items los re-aplican a tokens Infinity.
	 */
	'--preview-feedback-info-surface'?: string;
	'--preview-feedback-on-info-surface'?: string;
	'--preview-feedback-warning-surface'?: string;
	'--preview-feedback-on-warning-surface'?: string;
	'--preview-feedback-success-surface'?: string;
	'--preview-feedback-on-success-surface'?: string;
	'--preview-feedback-error-surface'?: string;
	'--preview-feedback-on-error-surface'?: string;
	/** Campos (Infinity tokens). */
	'--border-control'?: string;
	'--border-control-active'?: string;
	'--border-control-selected'?: string;
	'--surface-selected'?: string;
	'--surface-selected-disabled'?: string;
	'--radius-control'?: string;
	'--input-selection-color'?: string;
	/**
	 * Espejos anti-`.dark` (Infinity redefine border/surface en el layout).
	 * `.preview-fields` los re-aplica a tokens Infinity.
	 */
	'--preview-border-control'?: string;
	'--preview-border-control-active'?: string;
	'--preview-border-control-selected'?: string;
	'--preview-surface-selected'?: string;
	'--preview-surface-selected-disabled'?: string;
	'--preview-radius-control'?: string;
	'--preview-text-highlight'?: string;
	/**
	 * Infinity: `.items__item--selected { color: var(--text-highlight) }`.
	 * Solo host del preview (inline gana a `.light` de Infinity).
	 */
	'--text-highlight'?: string;
	/** Tokens huérfanos brand_* (NIF-751) — índice abierto. */
	[orphanToken: `--${string}`]: string | undefined;
}

@Component({
	selector: 'app-design-theme-preview',
	imports: [
		TranslatePipe,
		ReactiveFormsModule,
		PwcsLayoutComponent,
		TopnavComponent,
		SelectComponent,
		ProgressComponent,
		WatermarkComponent,
		CollapseComponent,
		AlertComponent,
		ButtonComponent,
		TileComponent,
		CardAccessComponent,
		NavHelpComponent,
		LinkComponent,
		LottieComponent,
		SpinnerComponent,
		ScanFaceComponent,
		FormGroupComponent,
		InputComponent,
		ToggleComponent,
		CheckboxComponent,
		ItemComponent,
		ListDetailComponent,
		TagComponent,
	],
	templateUrl: './design-theme-preview.component.html',
	styleUrl: './design-theme-preview.component.scss',
})
export class DesignThemePreviewComponent implements OnInit, OnDestroy, OnChanges {
	@Input() themeData?: any;
	@Input() themeMode?: string; // 'light' | 'dark'
	/** Logo ya resuelto (URL o data-URL) desde el detalle; evita re-leer el File del form. */
	@Input() logoSrc?: string;
	/** Spinner personalizado resuelto (data-URL / URL) desde el detalle. */
	@Input() customSpinnerSrc?: string;
	/** Pestaña activa del editor; el mock interior cambia por sección. */
	@Input() section: DesignThemeSection = DesignThemeSection.GENERAL;

	private static readonly DEFAULT_LOGO = 'images/tecalis-lg.svg';
	private static readonly DEFAULT_LOGO_DARK = 'images/logo-tecalis-white.svg';
	private static readonly LOTTIE_LIGHT = 'assets/animations/pwcs-prueba-vida-light.json';
	private static readonly LOTTIE_DARK = 'assets/animations/pwcs-prueba-vida-dark.json';

	private renderer = inject(Renderer2);
	private elementRef = inject(ElementRef);
	private cdr = inject(ChangeDetectorRef);
	private http = inject(HttpClient);
	private sanitizer = inject(DomSanitizer);
	private fontLink?: HTMLLinkElement;
	private logoFromTheme?: string;
	private lottieSub?: Subscription;
	private graphicsSvgSub?: Subscription;
	/** Evita reiniciar ng-lottie en cada CD (themeData nuevo por getRawValue). */
	private lastLottieKey = '';
	private lastGraphicsSvgKey = '';

	readonly DesignThemeSection = DesignThemeSection;

	/** Progreso decorativo del mock (igual que una pantalla a mitad de journey). */
	public readonly previewProgress = 20;

	/**
	 * Progreso del óvalo `tec-scan-face` (0–100) para ver arcos de guía de captura.
	 * Alineado con PoL PWCS (`progressValue` + halfLeft).
	 */
	public readonly scanFacePreviewProgress = 35;

	public readonly languageControl = new FormControl<ISelect | string>(
		{ id: 'es', label: 'Español' },
		{ nonNullable: true },
	);

	public readonly languageOptions: ISelect[] = [
		{ id: 'es', label: 'Español' },
		{ id: 'en', label: 'English' },
		{ id: 'pt', label: 'Português' },
	];

	/** Mock Campos: ingreso + productos (toggle multi) + plan (select abierto). */
	public readonly fieldsIncomeControl = new FormControl('2,575€', { nonNullable: true });
	public readonly fieldsProductsControl = new FormControl<IToogleSelect[]>(
		[
			{ id: 'purchase_insurance', label: 'Seguro de compras' },
			{ id: 'life_insurance', label: 'Seguro de vida', disabled: true },
		],
		{ nonNullable: true },
	);
	public readonly fieldsPlanControl = new FormControl<ISelect>(
		{ id: 'monthly', label: 'Mensual' },
		{ nonNullable: true },
	);

	public readonly fieldsProductOptions: IToogleSelect[] = [
		{ id: 'physical_card', label: 'Tarjeta física' },
		{ id: 'purchase_insurance', label: 'Seguro de compras' },
		{ id: 'life_insurance', label: 'Seguro de vida', disabled: true },
	];

	public readonly fieldsPlanOptions: ISelect[] = [
		{ id: 'monthly', label: 'Mensual' },
		{ id: 'quarterly', label: 'Trimestral' },
		{ id: 'yearly', label: 'Anual' },
	];

	/** Mock Avanzado: «aportar más adelante» marcado (como Figma). */
	public readonly advancedDeferControl = new FormControl(true, { nonNullable: true });

	/** Tags del mock choose (banco / IRPF), alineados con PWCS. */
	public readonly chooseBankTag: ITagImage = { icon: 'icon-bank', status: 'notice' };
	public readonly chooseIrpfTag: ITagImage = { icon: 'icon-euro', status: 'default' };

	/** Tags del mock Marca (beneficios / tec-item): info → acento de marca (Carmen 123499). */
	public readonly brandBenefitPensionTag: ITagImage = { icon: 'icon-bank', status: 'default' };
	public readonly brandBenefitHealthTag: ITagImage = { icon: 'icon-smile', status: 'success' };
	public readonly brandBenefitFiscalTag: ITagImage = { icon: 'icon-file', status: 'info' };

	/** Nav lateral decorativo: idioma + ayuda (ayuda selected → estado activo del botón icono). */
	public readonly previewNavHelp: INavHelp[] = [
		{ id: 'language', icon: 'icon-language' },
		{ id: 'help', icon: 'icon-help-circle', selected: true },
	];

	/** Opciones Lottie del tile Grafismos (prueba de vida). */
	public graphicsLottieOptions: AnimationOptions | null = null;

	/** SVG inline de home PoL y tutorial escrituras (Grafismos). */
	public graphicsPolSvg: SafeHtml | null = null;
	public graphicsDocsSvg: SafeHtml | null = null;

	get previewLogo(): string {
		return (
			this.logoSrc ||
			this.logoFromTheme ||
			(this.isDarkMode ? DesignThemePreviewComponent.DEFAULT_LOGO_DARK : DesignThemePreviewComponent.DEFAULT_LOGO)
		);
	}

	/** URL del spinner: input del padre o string/File del tema. */
	get previewSpinnerSrc(): string {
		if (this.customSpinnerSrc) {
			return this.customSpinnerSrc;
		}
		const spinner = this.themeData?.custom_spinner;
		return typeof spinner === 'string' && spinner ? spinner : '';
	}

	get isDarkMode(): boolean {
		return (this.themeMode || '').toString().toLowerCase() === 'dark';
	}

	/** Imagen tutorial OCR (doc-reverse), igual que pwcs2-new. */
	get linksTutorialImageSrc(): string {
		return this.isDarkMode ? 'assets/images/doc-reverse-dark.png' : 'assets/images/doc-reverse.png';
	}

	get isTextPreview(): boolean {
		return this.section === DesignThemeSection.TEXT;
	}

	get isButtonsPreview(): boolean {
		return this.section === DesignThemeSection.BUTTONS;
	}

	get isLinksPreview(): boolean {
		return this.section === DesignThemeSection.LINKS;
	}

	get isGraphicsPreview(): boolean {
		return this.section === DesignThemeSection.GRAPHICS;
	}

	get isLoadingPreview(): boolean {
		return this.section === DesignThemeSection.FORMS;
	}

	get isFieldsPreview(): boolean {
		return this.section === DesignThemeSection.BORDERS;
	}

	get isAdvancedPreview(): boolean {
		return this.section === DesignThemeSection.ADVANCED;
	}

	ngOnInit(): void {
		this.loadGoogleFonts();
		this.applyThemeMode();
		if (this.isGraphicsPreview) {
			this.reloadGraphicsAssets();
		}
	}

	ngOnDestroy(): void {
		if (this.fontLink) document.head.removeChild(this.fontLink);
		this.lottieSub?.unsubscribe();
		this.graphicsSvgSub?.unsubscribe();
		this.clearBodySelectSelectionStyles();
	}

	private loadGoogleFonts(): void {
		const preconnect1 = document.createElement('link');
		preconnect1.rel = 'preconnect';
		preconnect1.href = 'https://fonts.googleapis.com';
		document.head.appendChild(preconnect1);

		const preconnect2 = document.createElement('link');
		preconnect2.rel = 'preconnect';
		preconnect2.href = 'https://fonts.gstatic.com';
		preconnect2.crossOrigin = 'anonymous';
		document.head.appendChild(preconnect2);

		this.fontLink = document.createElement('link');
		this.fontLink.rel = 'stylesheet';
		this.fontLink.href =
			'https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&family=Open+Sans:wght@300;400;500;600;700&family=Poppins:wght@300;400;500;600;700&family=Roboto:wght@300;400;500;700&family=Lato:wght@300;400;700&display=swap';
		document.head.appendChild(this.fontLink);
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['themeData'] && this.themeData) {
			this.convertLogoToBase64();
			this.updateStyles(this.themeData);
		}
		if (changes['themeMode']) this.applyThemeMode();
		if (this.isGraphicsPreview && (changes['themeData'] || changes['themeMode'] || changes['section'])) {
			this.reloadGraphicsAssets();
		}
	}

	private convertLogoToBase64(): void {
		// Si el padre ya pasa logoSrc, no hace falta re-leer el File del form.
		if (this.logoSrc) {
			this.logoFromTheme = undefined;
			return;
		}

		const logo = this.themeData?.logo;
		if (logo instanceof File) {
			const reader = new FileReader();
			reader.onload = () => {
				this.logoFromTheme = reader.result as string;
				this.cdr.markForCheck();
			};
			reader.readAsDataURL(logo);
		} else if (typeof logo === 'string' && logo) {
			this.logoFromTheme = logo;
		} else {
			this.logoFromTheme = undefined;
		}
	}

	private colorValue(colorData: any): string {
		if (!colorData) return '';
		if (typeof colorData === 'object') {
			const raw = typeof colorData.color === 'string' && colorData.color !== '' ? colorData.color : colorData.id;
			if (raw == null) return '';
			return String(raw).replace(/^"(.*)"$/, '$1');
		}
		return String(colorData).replace(/^"(.*)"$/, '$1');
	}

	private reloadGraphicsAssets(): void {
		this.reloadGraphicsLottie();
		this.reloadGraphicsSvgs();
	}

	private reloadGraphicsSvgs(): void {
		const polPath = this.isDarkMode ? 'assets/images/home-pol-dark.svg' : 'assets/images/home-pol.svg';
		const docsPath = this.isDarkMode
			? 'assets/images/connection-escrituras-tutorial-dark.svg'
			: 'assets/images/connection-escrituras-tutorial.svg';
		const key = `${polPath}|${docsPath}`;
		if (key === this.lastGraphicsSvgKey && this.graphicsPolSvg && this.graphicsDocsSvg) {
			return;
		}
		this.lastGraphicsSvgKey = key;
		this.graphicsSvgSub?.unsubscribe();
		this.graphicsSvgSub = forkJoin({
			pol: this.http.get(polPath, { responseType: 'text' }).pipe(catchError(() => of(''))),
			docs: this.http.get(docsPath, { responseType: 'text' }).pipe(catchError(() => of(''))),
		}).subscribe(({ pol, docs }) => {
			this.graphicsPolSvg = pol ? this.sanitizer.bypassSecurityTrustHtml(pol) : null;
			this.graphicsDocsSvg = docs ? this.sanitizer.bypassSecurityTrustHtml(docs) : null;
			this.cdr.markForCheck();
		});
	}

	private reloadGraphicsLottie(): void {
		const path = this.isDarkMode
			? DesignThemePreviewComponent.LOTTIE_DARK
			: DesignThemePreviewComponent.LOTTIE_LIGHT;
		const data = this.themeData || {};
		const border = this.colorValue(data.icon_border_color);
		const iconBorder = this.colorValue(data.icon_primary_color);
		const background = this.colorValue(data.icon_background_color);
		const secondaryBorder = this.colorValue(data.icon_secondary_border_color);
		const key = `${path}|${border}|${iconBorder}|${background}|${secondaryBorder}`;
		// Misma clave → no tocar options: ngx-lottie reinicia al cambiar la referencia.
		if (key === this.lastLottieKey && this.graphicsLottieOptions) {
			return;
		}
		this.lastLottieKey = key;
		this.lottieSub?.unsubscribe();
		this.lottieSub = this.http.get(path).subscribe({
			next: (raw) => {
				// Igual que LottieThemeService: solo mutar JSON si hay override opaco.
				const animationData = themeLottieAnimationData(raw, {
					border,
					iconBorder,
					background,
					secondaryBorder,
				});
				this.graphicsLottieOptions = { animationData, loop: true, autoplay: true };
				this.cdr.markForCheck();
			},
			error: () => {
				this.graphicsLottieOptions = { path, loop: true, autoplay: true };
				this.cdr.markForCheck();
			},
		});
	}

	private updateStyles(data: any): void {
		const getValue = (colorData: any): string => this.colorValue(colorData);
		const px = (value: unknown): string => (value != null && value !== '' ? `${value}px` : '');

		const primaryRadius = px(data.border_round);
		const secondaryRadius = px(data.border_round_secondary) || primaryRadius;

		const textStrong = getValue(data.text_color_primary) || getValue(data.text_color);
		const textTitle = getValue(data.text_color_title);
		const textSecondary = getValue(data.text_color_secondary);
		const textTertiary = getValue(data.text_color_tertiary);

		const styles: IThemeStyles = {
			'--typography': getValue(data.text_typography),
			/** Compat preview legado; mismo hex que --text-strong. */
			'--text-color': textStrong,
			/** Anti-`.dark`: subtítulo / stepper leen esto antes que `--text-strong`. */
			'--text-color-primary': textStrong,
			'--text-color-title': textTitle,
			'--text-color-secondary': textSecondary,
			'--text-color-tertiary': textTertiary,
			'--text-strong': textStrong,
			'--text-primary': textTitle,
			'--text-secondary': textSecondary,
			'--text-tertiary': textTertiary,
			'--button-primary-text-color': getValue(data.button_primary_text_color),
			'--button-primary-text-hover-color': getValue(data.button_primary_text_color_hover),
			'--button-primary-border-color': getValue(data.button_primary_border_color),
			'--button-primary-border-hover-color': getValue(data.button_primary_border_color_hover),
			'--button-primary-background-color': getValue(data.button_primary_background_color),
			'--button-primary-background-hover-color': getValue(data.button_primary_background_color_hover),
			'--button-secondary-text-color': getValue(data.button_secondary_text_color),
			'--button-secondary-text-hover-color': getValue(data.button_secondary_text_color_hover),
			'--button-secondary-border-color': getValue(data.button_secondary_border_color),
			'--button-secondary-border-hover-color': getValue(data.button_secondary_border_color_hover),
			'--button-secondary-background-color': getValue(data.button_secondary_background_color),
			'--button-secondary-background-hover-color': getValue(data.button_secondary_background_color_hover),
			'--button-border-radius': px(data.button_border_round),
			'--button-icon-border-radius': px(data.button_icon_border_round),
			'--button-icon-color': getValue(data.button_icon_color),
			'--action-on-icon': getValue(data.button_icon_color),
			'--button-icon-color-hover': getValue(data.button_icon_color_hover),
			'--button-icon-color-active': getValue(data.button_icon_color_active),
			'--button-icon-background-color': getValue(data.button_icon_background_color),
			'--button-icon-background-color-hover': getValue(data.button_icon_background_color_hover),
			'--button-icon-background-color-active': getValue(data.button_icon_background_color_active),
			'--link-color': getValue(data.link_color),
			'--link-hover-color': getValue(data.link_color_hover),
			'--link-text-decoration':
				data.links_underline === true || data.links_underline === 'underline' ? 'underline' : 'none',
			'--link-text-decoration-hover':
				data.links_underline_hover === true || data.links_underline_hover === 'underline'
					? 'underline'
					: 'none',
			'--action-link': getValue(data.link_color),
			'--action-link-hover': getValue(data.link_color_hover),
			/** `tec-checkbox` checked usa `--action-primary` / `--action-on-primary` (no `--button-primary-*`). */
			'--action-primary': getValue(data.button_primary_background_color),
			'--action-primary-border': getValue(data.button_primary_border_color),
			'--action-primary-hover': getValue(data.button_primary_background_color_hover),
			'--action-on-primary': getValue(data.button_primary_text_color),
			'--general-border-radius': primaryRadius,
			'--pwcs-radius-general': primaryRadius,
			'--radius-container': primaryRadius,
			'--radius-container-sm': secondaryRadius,
			'--radius-tag': px(data.tag_border_round),
			'--radius-checkbox': px(data.checkbox_border_round),
			'--icon-border': getValue(data.icon_border_color),
			'--surface-inverse': getValue(data.icon_border_color),
			'--icon-background': getValue(data.icon_background_color),
			'--surface-brand-primary': getValue(data.icon_background_color),
			'--icon-primary': getValue(data.icon_primary_color),
			'--icon-highlight': getValue(data.icon_primary_color),
			'--icon-brand-primary': getValue(data.icon_primary_color),
			'--icon-secondary-border': getValue(data.icon_secondary_border_color),
			'--icon-secondary-background': getValue(data.icon_secondary_background_color),
			'--surface-brand-tertiary': getValue(data.icon_secondary_background_color),
			'--icon-secondary': getValue(data.icon_secondary_color),
			'--icon-brand-tertiary': getValue(data.icon_secondary_color),
			'--pwcs-icon-border': getValue(data.icon_border_color),
			'--pwcs-icon-background': getValue(data.icon_background_color),
			'--pwcs-icon-primary': getValue(data.icon_primary_color),
			/** Óvalo Lottie / trazo SVG; no `--surface-brand-secondary` (fondo tag-img-info DS). */
			'--pwcs-icon-secondary-border': getValue(data.icon_secondary_border_color),
			'--pwcs-icon-secondary-background': getValue(data.icon_secondary_background_color),
			'--pwcs-icon-secondary': getValue(data.icon_secondary_color),
			/** Lottie = grafismos (borde secundario → óvalo de puntos). */
			'--lottie-frame-border': getValue(data.icon_border_color),
			'--lottie-frame-icon-border': getValue(data.icon_primary_color),
			'--lottie-frame-bg': getValue(data.icon_background_color),
			'--pwcs-lottie-frame-border': getValue(data.icon_border_color),
			'--pwcs-lottie-frame-icon-border': getValue(data.icon_primary_color),
			'--pwcs-lottie-frame-bg': getValue(data.icon_background_color),
			'--pwcs-progress-fill': getValue(data.progress_fill_color),
			'--brand-primary': getValue(data.progress_fill_color),
			'--progress-border-radius': px(data.progress_border_round),
			'--feedback-info-surface': getValue(data.feedback_info_surface),
			'--feedback-on-info-surface': getValue(data.feedback_on_info_surface),
			'--feedback-info-solid': getValue(data.capture_guide_color),
			'--capture-guide-color': getValue(data.capture_guide_color),
			'--feedback-warning-surface': getValue(data.feedback_warning_surface),
			'--feedback-on-warning-surface': getValue(data.feedback_on_warning_surface),
			'--feedback-success-surface': getValue(data.feedback_success_surface),
			'--feedback-on-success-surface': getValue(data.feedback_on_success_surface),
			'--feedback-error-surface': getValue(data.feedback_error_surface),
			'--feedback-on-error-surface': getValue(data.feedback_on_error_surface),
			/** Espejos: Marca/Avanzado / `.dark` no pisan estos nombres. */
			'--preview-feedback-info-surface': getValue(data.feedback_info_surface),
			'--preview-feedback-on-info-surface': getValue(data.feedback_on_info_surface),
			'--preview-feedback-warning-surface': getValue(data.feedback_warning_surface),
			'--preview-feedback-on-warning-surface': getValue(data.feedback_on_warning_surface),
			'--preview-feedback-success-surface': getValue(data.feedback_success_surface),
			'--preview-feedback-on-success-surface': getValue(data.feedback_on_success_surface),
			'--preview-feedback-error-surface': getValue(data.feedback_error_surface),
			'--preview-feedback-on-error-surface': getValue(data.feedback_on_error_surface),
			'--border-control': getValue(data.input_border_color),
			'--border-control-active': getValue(data.input_border_color_focus),
			'--border-control-selected': getValue(data.input_selection_color),
			'--surface-selected': getValue(data.toggle_background_color_active),
			'--surface-selected-disabled': getValue(data.toggle_background_color_active_disabled),
			'--radius-control': px(data.input_border_round),
			'--input-selection-color': getValue(data.input_selection_color),
			/** Ítem activo del select (Infinity) + espejo body. */
			'--text-highlight': getValue(data.input_selection_color),
			/** Espejos: `.preview-fields` / `.dark` no pisan estos nombres. */
			'--preview-border-control': getValue(data.input_border_color),
			'--preview-border-control-active': getValue(data.input_border_color_focus),
			'--preview-border-control-selected': getValue(data.input_selection_color),
			'--preview-surface-selected': getValue(data.toggle_background_color_active),
			'--preview-surface-selected-disabled': getValue(data.toggle_background_color_active_disabled),
			'--preview-radius-control': px(data.input_border_round),
			'--preview-text-highlight': getValue(data.input_selection_color),
		};

		const skin = (this.themeMode || 'light').toString().toLowerCase() === 'dark' ? 'dark' : 'light';
		const orphanMap = DesignHelperService.getBrandOrphanSemantics(skin);
		for (const brandKey of DesignHelperService.BRAND_KEYS) {
			const tokens = orphanMap[brandKey];
			if (!tokens?.length) {
				continue;
			}
			const hex = getValue(data[brandKey]);
			if (!hex) {
				continue;
			}
			for (const token of tokens) {
				styles[token as keyof IThemeStyles] = hex;
			}
		}

		const element = this.elementRef.nativeElement;

		Object.keys(styles).forEach((key) => {
			const value = styles[key as keyof IThemeStyles];
			if (value) {
				element.style.setProperty(key, value);
			}
		});

		this.syncBodySelectSelectionStyles(getValue(data.input_selection_color));
	}

	/**
	 * Infinity mueve `.select__menu` a `document.body` al abrir (positionMenuOverlay).
	 * Las CSS vars del host del preview no heredan allí: espejo en body + clase global.
	 */
	private syncBodySelectSelectionStyles(selectionColor: string): void {
		const body = document.body;
		if (!selectionColor) {
			this.clearBodySelectSelectionStyles();
			return;
		}
		body.classList.add('design-theme-preview--selection');
		body.style.setProperty('--design-preview-selection-color', selectionColor);
	}

	private clearBodySelectSelectionStyles(): void {
		const body = document.body;
		body.classList.remove('design-theme-preview--selection');
		body.style.removeProperty('--design-preview-selection-color');
	}

	private applyThemeMode(): void {
		const el = this.elementRef.nativeElement;
		const mode = (this.themeMode || 'light').toString().toLowerCase();
		this.renderer.removeClass(el, 'light');
		this.renderer.removeClass(el, 'dark');
		const clazz = mode === 'dark' ? 'dark' : 'light';
		this.renderer.addClass(el, clazz);
		this.renderer.setAttribute(el, 'data-theme', mode);
	}
}
